import * as React from 'react';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
// import CircularProgress from '@mui/material/CircularProgress';
import { CloseIcon } from '../../../assets/CommonSvg';
import FileViewer from 'react-file-viewer';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: "50%",
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: "10px",
    py: "15px"
};

export default function FilePreviewModal({ fileURL, open, setOpen, ModalTitle }) {
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState(false);
    const [zoom, setZoom] = React.useState(1);
    const [position, setPosition] = React.useState({ x: 0, y: 0 });
    const [dragging, setDragging] = React.useState(false);
    const dragStart = React.useRef({ x: 0, y: 0 });


const zoomIn = () => setZoom(prev => Math.min(prev + 0.2, 3));
const zoomOut = () => setZoom(prev => Math.max(prev - 0.2, 1));

const resetZoom = () => {
  setZoom(1);
  setPosition({ x: 0, y: 0 });
};

const onMouseDown = (e) => {
  if (zoom <= 1) return;
  setDragging(true);
  dragStart.current = {
    x: e.clientX - position.x,
    y: e.clientY - position.y,
  };
};

const onMouseMove = (e) => {
  if (!dragging) return;
  setPosition({
    x: e.clientX - dragStart.current.x,
    y: e.clientY - dragStart.current.y,
  });
};

const stopDragging = () => setDragging(false);


    const handleClose = () => {
        setOpen(false);
        setIsLoading(true);
        setError(false);
    };

    const getFileExtension = (url) => {
        return url?.split('.').pop().toLowerCase()?.split('?')[0];
    };

    const fileExtension = getFileExtension(fileURL);
    const supportedFileTypes = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'txt', 'csv'];

    const renderPreview = () => {
        if (!fileURL) return null;

        if (supportedFileTypes.includes(fileExtension) && !error) {
      return ( <>
    <div
  style={{
    height: '400px',
    overflow: 'hidden', // ✅ NO SCROLLBAR
    background: '#f9f9f9',
    cursor: zoom > 1 ? (dragging ? 'grabbing' : 'grab') : 'default',
  }}
  onMouseDown={onMouseDown}
  onMouseMove={onMouseMove}
  onMouseUp={stopDragging}
  onMouseLeave={stopDragging}
>
  <div
    style={{
      height: '300px',
      transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
      transformOrigin: 'top left',
      transition: dragging ? 'none' : 'transform 0.2s ease',
      width: '100%',
    }}
  >
    <FileViewer
      fileType={fileExtension}
      filePath={fileURL}
      onError={(e) => {
        console.error('FileViewer error:', e);
        setError(true);
        setIsLoading(false);
      }}
      onLoad={() => setIsLoading(false)}
    />
  </div>
</div>
<div className="flex justify-center gap-3 mb-2">
  <button onClick={zoomOut} className="px-3 py-1 border rounded">−</button>
  <button onClick={resetZoom} className="px-3 py-1 border rounded">Reset</button>
  <button onClick={zoomIn} className="px-3 py-1 border rounded">+</button>
</div>
</>
  );
        } else if (['doc', 'docx', 'xls', 'xlsx'].includes(fileExtension)) {
            // fallback to Office viewer
            return (
                <iframe
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(fileURL)}`}
                    title="Document Preview"
                    width="100%"
                    height="500px"
                    className="rounded-md"
                    onLoad={() => setIsLoading(false)}
                    style={{ display: isLoading ? 'none' : 'block', zIndex: 99999 }}
                />
            );
        } else {
            return (
                <div className="flex justify-center items-center h-[500px]">
                    <Typography variant="body1">
                        Preview not available for this file type. Please download the file to view it.
                    </Typography>
                </div>
            );
        }
    };

    React.useEffect(() => {
        if (fileURL) {
            setIsLoading(true);
            setError(false);
        }
    }, [fileURL]);

    return (
        <div>
            <Modal
                aria-labelledby="transition-modal-title"
                aria-describedby="transition-modal-description"
                open={open || false}
                onClose={handleClose}
                closeAfterTransition
                slots={{ backdrop: Backdrop }}
                slotProps={{
                    backdrop: {
                        timeout: 500,
                    },
                }}
            >
                <Fade in={open}>
                    <div className='relative h-full w-full'>
                        <Box sx={style} className="relative">
                            {ModalTitle && (
                                <div className='pb-[15px] border-b border-[#DDDDDD]'>
                                    <Typography id="transition-modal-title" variant="h6" textAlign={'center'} component="h6">
                                        {ModalTitle}
                                    </Typography>
                                </div>
                            )}

                            {/* {isLoading && (
                                <div className="flex justify-center items-center" style={{ height: '500px' }}>
                                    <CircularProgress />
                                </div>
                            )} */}

                            {renderPreview()}

                            <div onClick={handleClose} className="absolute right-[-27px] top-[-17px] cursor-pointer">
                                <Tooltip title="Close"><div><CloseIcon fill="#b82018" /></div></Tooltip>
                            </div>
                        </Box>
                    </div>
                </Fade>
            </Modal>
        </div>
    );
}

FilePreviewModal.propTypes = {
    fileURL: PropTypes.string,
    open: PropTypes.bool,
    setOpen: PropTypes.func,
    ModalTitle: PropTypes.string,
};
